import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore'
import { db } from '../config/firebase'

// Sınav Sonucu Interface
export interface FirebaseExamResult {
  id: string
  userId: string
  examName: string
  date: string
  type: 'LGS' | 'Deneme'
  institution?: string
  scores: {
    'Türkçe': number
    'Matematik': number
    'Fen Bilimleri': number
    'T.C. İnkılap Tarihi': number
    'İngilizce': number
    'Din Kültürü': number
  }
  correctAnswers: {
    'Türkçe': number
    'Matematik': number
    'Fen Bilimleri': number
    'T.C. İnkılap Tarihi': number
    'İngilizce': number
    'Din Kültürü': number
  }
  wrongAnswers: {
    'Türkçe': number
    'Matematik': number
    'Fen Bilimleri': number
    'T.C. İnkılap Tarihi': number
    'İngilizce': number
    'Din Kültürü': number
  }
  totalScore: number
  ranking?: number
  totalParticipants?: number
  createdAt: string
  updatedAt: string
}

// Firestore Collection
const EXAM_RESULTS_COLLECTION = 'examResults'

// Güvenli Firestore operasyonu
const safeFirestoreOperation = async <T>(
  operation: () => Promise<T>,
  retries: number = 3,
  delay: number = 1000
): Promise<T | null> => {
  for (let i = 0; i < retries; i++) {
    try {
      return await operation()
    } catch (error) {
      console.error(`Firestore operation failed (attempt ${i + 1}):`, error)
      if (i === retries - 1) {
        throw error
      }
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }
  return null
}

export const examResultsService = {
  // Yeni sınav sonucu ekleme
  createExamResult: async (examData: Omit<FirebaseExamResult, 'id' | 'createdAt' | 'updatedAt'>) => {
    return safeFirestoreOperation(async () => {
      const docRef = await addDoc(collection(db, EXAM_RESULTS_COLLECTION), {
        ...examData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
      return docRef.id
    })
  },

  // Kullanıcının tüm sınav sonuçlarını getirme
  getUserExamResults: async (userId: string): Promise<FirebaseExamResult[]> => {
    return safeFirestoreOperation(async () => {
      const q = query(
        collection(db, EXAM_RESULTS_COLLECTION),
        where('userId', '==', userId)
      )
      const querySnapshot = await getDocs(q)
      const results = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
      })) as FirebaseExamResult[]
      
      // Client-side sorting by date (newest first)
      return results.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    }) || []
  },

  // Belirli bir sınav sonucunu getirme
  getExamResult: async (examId: string): Promise<FirebaseExamResult | null> => {
    return safeFirestoreOperation(async () => {
      const docRef = doc(db, EXAM_RESULTS_COLLECTION, examId)
      const docSnap = await getDoc(docRef)
      if (docSnap.exists()) {
        const data = docSnap.data()
        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
        } as FirebaseExamResult
      }
      return null
    })
  },

  // Sınav sonucunu güncelleme
  updateExamResult: async (examId: string, examData: Partial<Omit<FirebaseExamResult, 'id' | 'createdAt' | 'updatedAt'>>) => {
    return safeFirestoreOperation(async () => {
      const docRef = doc(db, EXAM_RESULTS_COLLECTION, examId)
      await updateDoc(docRef, {
        ...examData,
        updatedAt: serverTimestamp()
      })
      return true
    })
  },

  // Sınav sonucunu silme
  deleteExamResult: async (examId: string) => {
    return safeFirestoreOperation(async () => {
      const docRef = doc(db, EXAM_RESULTS_COLLECTION, examId)
      await deleteDoc(docRef)
      return true
    })
  },

  // Kullanıcının sınav türüne göre sonuçlarını getirme
  getUserExamResultsByType: async (userId: string, type: 'LGS' | 'Deneme'): Promise<FirebaseExamResult[]> => {
    return safeFirestoreOperation(async () => {
      const q = query(
        collection(db, EXAM_RESULTS_COLLECTION),
        where('userId', '==', userId),
        where('type', '==', type)
      )
      const querySnapshot = await getDocs(q)
      const results = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
      })) as FirebaseExamResult[]
      
      // Client-side sorting by date (newest first)
      return results.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    }) || []
  },

  // Kullanıcının en son sınav sonuçlarını getirme (limit ile)
  getRecentExamResults: async (userId: string, limitCount: number = 10): Promise<FirebaseExamResult[]> => {
    return safeFirestoreOperation(async () => {
      const q = query(
        collection(db, EXAM_RESULTS_COLLECTION),
        where('userId', '==', userId)
      )
      const querySnapshot = await getDocs(q)
      const results = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
      })) as FirebaseExamResult[]
      
      // Client-side sorting by date (newest first) and limit
      return results
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, limitCount)
    }) || []
  },

  // Gerçek zamanlı sınav sonuçları dinleme
  listenToUserExamResults: (userId: string, callback: (results: FirebaseExamResult[]) => void) => {
    const q = query(
      collection(db, EXAM_RESULTS_COLLECTION),
      where('userId', '==', userId)
    )
    
    return onSnapshot(q, (querySnapshot) => {
      const results = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
      })) as FirebaseExamResult[]
      
      // Client-side sorting by date (newest first)
      const sortedResults = results.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      callback(sortedResults)
    }, (error) => {
      console.error('Error listening to exam results:', error)
      callback([])
    })
  }
}

export default examResultsService